import { onMounted, onUnmounted, toValue, watch, type MaybeRefOrGetter } from 'vue'

interface RefreshOptions {
  minIntervalMs?: number
  intervalMs?: MaybeRefOrGetter<number>
}

// Runs on mount, when the tab or window comes back (at most once per minIntervalMs),
// and every intervalMs while visible when set; 0 stops the timer.
export function useRefreshOnVisible(load: () => unknown, { minIntervalMs = 60_000, intervalMs = 0 }: RefreshOptions = {}) {
  let lastAt = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let mounted = false

  function run(force = false) {
    const now = Date.now()
    if (!force && now - lastAt < minIntervalMs) return
    lastAt = now
    void load()
  }

  function onVisible() {
    if (document.visibilityState === 'visible') run()
  }

  function schedule() {
    clearTimeout(timer)
    timer = undefined
    const ms = toValue(intervalMs)
    if (!mounted || ms <= 0) return
    timer = setTimeout(() => {
      if (document.visibilityState === 'visible') run(true)
      schedule()
    }, ms)
  }

  watch(() => toValue(intervalMs), schedule)

  onMounted(() => {
    mounted = true
    run(true)
    schedule()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
  })
  onUnmounted(() => {
    mounted = false
    clearTimeout(timer)
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('focus', onVisible)
  })
}
